/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
/* TODO: Add proper typing for API responses to remove these eslint-disable comments */

import { defineStore } from 'pinia'
import { isLocalStorageAvailable } from '~/utility/isLocalStorageAvailable'

export const useSeenCampsitesStore = defineStore('seenCampsitesStore', () => {
  const storageKey = 'seenMapCampsites'
  const slugs = ref<Set<string>>(new Set())
  const seenCampsites = ref<string[]>([])
  const nowTimestamp = Date.now()

  function get(): string[] {
    return Array.from(slugs.value)
  }

  function resetStorage() {
    if (!isLocalStorageAvailable) {
      return
    }
    let data: { timestamp: number; slugs: string[] } | null = null
    try {
      const raw = localStorage.getItem(storageKey)
      data = raw ? JSON.parse(raw) : null
    } catch {
      data = null
    }

    const isValidArray = Array.isArray(data?.slugs)
    const ts = typeof data?.timestamp === 'number' ? data.timestamp : 0

    if (!isValidArray || !ts || keyExpired(ts)) {
      localStorage.removeItem(storageKey)
      try {
        localStorage.setItem(storageKey, JSON.stringify(getEmptyStorageObj()))
      } catch {
        // Storage full or unavailable, continue with in-memory only
      }
    } else {
      data!.timestamp = nowTimestamp
      try {
        localStorage.setItem(storageKey, JSON.stringify(data))
      } catch {
        // Storage full or unavailable, continue with in-memory only
      }
    }
  }

  function keyExpired(ts: unknown): boolean {
    if (typeof ts !== 'number' || !Number.isFinite(ts)) {
      return true
    }
    const diff = nowTimestamp - ts
    // 1 day = 24hours*60min*60sec*1000millisec
    const expirationLength = 24 * 60 * 60 * 1000
    return diff > expirationLength
  }

  function getEmptyStorageObj() {
    return {
      timestamp: nowTimestamp,
      slugs: [],
    }
  }

  function getSlugsFromStorage(): Set<string> {
    if (!isLocalStorageAvailable) {
      return new Set()
    }
    const rawData = localStorage.getItem(storageKey)
    if (!rawData) {
      return new Set()
    }
    try {
      const storageObj = JSON.parse(rawData)
      return new Set(storageObj?.slugs || [])
    } catch {
      return new Set()
    }
  }

  function add(slug: string) {
    if (!isLocalStorageAvailable) {
      return
    }
    slugs.value.add(slug)
    const rawData = localStorage.getItem(storageKey)
    let storageObj: { timestamp: number; slugs: string[] } | null
    try {
      storageObj = rawData ? JSON.parse(rawData) : null
    } catch {
      storageObj = null
    }
    if (!storageObj || !Array.isArray(storageObj.slugs)) {
      storageObj = getEmptyStorageObj()
    }

    // Only add if not already present to prevent duplicates
    if (!storageObj.slugs.includes(slug)) {
      storageObj.slugs.push(slug)
      try {
        localStorage.setItem(storageKey, JSON.stringify(storageObj))
      } catch {
        // Storage full or unavailable, continue with in-memory only
      }
    }

    seenCampsites.value = get()
  }

  onMounted(() => {
    resetStorage()
    slugs.value = getSlugsFromStorage()
    seenCampsites.value = get()

    // This is required to sync changes across browser tabs
    // otherwise stores will become out of sync
    window.addEventListener('storage', (event) => {
      if (event.key === storageKey) {
        slugs.value = getSlugsFromStorage()
        seenCampsites.value = get()
      }
    })
  })
  return { seenCampsites, add, slugs, resetStorage }
})
